import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { MutationError } from '../components/MutationError';
import { useProject } from '../projects/context';
import { projectKeys, useProjectApi } from '../projects/useProjectApi';

// ContactStop: Kontaktstopp im Gespräch („bitte nicht mehr anrufen“, OFFENE_FRAGEN B-19/B-20) – für die Person und ihre
// Nummern oder, wenn der Kunde „uns“ meint, für den ganzen Haushalt. Die Nummern kommen auf die Sperrliste; aufheben
// kann nur, wer Kontaktregeln übersteuern darf, und nur an diesem Lead.
export function ContactStop({ leadId }: { leadId: string }) {
  const { project, can } = useProject();
  const papi = useProjectApi();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('Kunde möchte nicht mehr angerufen werden');
  const [scope, setScope] = useState<'PERSON' | 'HOUSEHOLD'>('PERSON');
  const owners = useQuery({ queryKey: projectKeys.leadOwners(project.id, leadId), queryFn: ({ signal }) => papi.leadOwners(leadId, signal) });
  const members = owners.data?.household?.members.length ?? 0;
  const stop = useMutation({
    mutationFn: () => papi.contactState(leadId, 'BLOCK', reason.trim(), members > 0 ? scope : 'PERSON'),
    onSuccess: () => {
      setOpen(false);
      void queryClient.invalidateQueries({ queryKey: projectKeys.focus(project.id, leadId) });
      void queryClient.invalidateQueries({ queryKey: projectKeys.leadOwners(project.id, leadId) });
    },
  });
  if (!can('call.start')) return null;
  if (stop.isSuccess) {
    return (
      <p className="alert alert-success" role="status">
        Kontaktstopp gesetzt{scope === 'HOUSEHOLD' && members > 0 ? ' – für den ganzen Haushalt' : ''}: Die Nummer steht auf der Sperrliste und wird nicht
        mehr angerufen. Bitte noch das Gesprächsergebnis wählen.
      </p>
    );
  }
  if (!open) {
    return (
      <button type="button" className="btn btn-small btn-ghost" onClick={() => setOpen(true)}>
        Kunde will nicht mehr angerufen werden (Kontaktstopp) …
      </button>
    );
  }
  return (
    <div className="inline-panel contact-stop">
      <label className="field wide">
        <span>Grund</span>
        <input value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} required />
      </label>
      {members > 0 && (
        <fieldset className="kind-choice">
          <legend>Für wen?</legend>
          <label className="radio">
            <input type="radio" name={`stop-${leadId}`} checked={scope === 'PERSON'} onChange={() => setScope('PERSON')} /> Diese Person und ihre
            Nummern
          </label>
          <label className="radio">
            <input type="radio" name={`stop-${leadId}`} checked={scope === 'HOUSEHOLD'} onChange={() => setScope('HOUSEHOLD')} /> Ganzer Haushalt („ruft
            uns nicht mehr an“) – mit {members === 1 ? '1 weiteren Person' : `${members} weiteren Personen`}
          </label>
        </fieldset>
      )}
      {stop.isError && <MutationError error={stop.error} />}
      <div className="form-actions">
        <button type="button" className="btn btn-danger" disabled={!reason.trim() || stop.isPending} onClick={() => stop.mutate()}>
          Kontaktstopp setzen
        </button>
        <button type="button" className="btn btn-ghost" onClick={() => setOpen(false)}>
          Abbrechen
        </button>
      </div>
    </div>
  );
}
